import { subDays, format } from 'date-fns';

// Generate mock historical price data for the last 30 days
export const generateMockPriceData = () => {
  const data = [];
  const basePrice = 0.52;
  
  for (let i = 29; i >= 0; i--) {
    const date = subDays(new Date(), i);
    const volatility = Math.random() * 0.1 - 0.05; // -5% to +5%
    const newsImpact = Math.random() * 0.15 - 0.075; // News impact
    
    const price = basePrice + volatility + newsImpact + (Math.sin(i / 7) * 0.02);
    
    data.push({
      date: format(date, 'yyyy-MM-dd'),
      price: Math.max(0.1, Number(price.toFixed(4))),
      volume: Math.floor(Math.random() * 1000000000) + 500000000,
      change: volatility,
      newsImpact: newsImpact
    });
  }
  
  return data;
};

// Generate mock news data for the last 30 days
export const generateMockNewsData = () => {
  const newsTypes = [
    { type: 'regulatory', impact: 0.15 },
    { type: 'partnership', impact: 0.08 },
    { type: 'technical', impact: 0.05 },
    { type: 'market', impact: 0.12 },
    { type: 'lawsuit', impact: -0.10 }
  ];

  const headlines = {
    regulatory: [
      "SEC Provides Clarity on XRP Classification",
      "New Regulatory Framework Favors XRP",
      "European Regulators Approve XRP Usage",
      "US Treasury Mentions XRP in Digital Asset Report"
    ],
    partnership: [
      "Major Bank Adopts XRP for Cross-Border Payments",
      "Ripple Partners with Fortune 500 Company",
      "New Exchange Lists XRP Trading Pairs",
      "Payment Processor Integrates XRP"
    ],
    technical: [
      "XRP Ledger Upgrade Improves Transaction Speed",
      "New XRP Wallet Features Announced",
      "Ripple Releases Technical Documentation",
      "XRP Network Reaches New Milestone"
    ],
    market: [
      "XRP Trading Volume Surges 200%",
      "Institutional Interest in XRP Growing",
      "XRP Price Analysis Shows Bullish Trend",
      "Market Maker Increases XRP Holdings"
    ],
    lawsuit: [
      "Ripple vs SEC Case Update",
      "Legal Expert Predicts XRP Lawsuit Outcome",
      "Court Filing Reveals New Evidence",
      "Settlement Discussions Reported"
    ]
  };

  const data = [];
  
  for (let i = 29; i >= 0; i--) {
    const date = subDays(new Date(), i);
    const newsCount = Math.floor(Math.random() * 3) + 1; // 1-3 news per day
    
    for (let j = 0; j < newsCount; j++) {
      const newsType = newsTypes[Math.floor(Math.random() * newsTypes.length)];
      const headlineOptions = headlines[newsType.type];
      const headline = headlineOptions[Math.floor(Math.random() * headlineOptions.length)];
      
      const sentiment = newsType.impact > 0 ? 'positive' : 
                       newsType.impact < 0 ? 'negative' : 'neutral';
      
      data.push({
        id: `news-${i}-${j}`,
        date: format(date, 'yyyy-MM-dd'),
        headline,
        sentiment,
        impact: newsType.impact + (Math.random() * 0.05 - 0.025),
        type: newsType.type,
        source: ['CoinDesk', 'Reuters', 'Bloomberg', 'CryptoNews'][Math.floor(Math.random() * 4)]
      });
    }
  }
  
  return data.sort((a, b) => new Date(b.date) - new Date(a.date));
};

// Generate price predictions for the next 7 days
export const generatePredictions = (historicalData, newsData) => {
  const predictions = [];
  const lastPrice = historicalData[historicalData.length - 1].price;
  
  // Calculate average news sentiment for recent days
  const recentNews = newsData.filter(news => {
    const newsDate = new Date(news.date);
    const cutoff = subDays(new Date(), 7);
    return newsDate >= cutoff;
  });
  
  const avgSentiment = recentNews.reduce((sum, news) => sum + news.impact, 0) / recentNews.length || 0;
  
  for (let i = 1; i <= 7; i++) {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + i);
    
    const trendFactor = avgSentiment * 0.5; // News sentiment influence
    const volatility = (Math.random() - 0.5) * 0.08; // Random volatility
    const momentum = i * 0.01; // Slight upward momentum
    
    const predictedPrice = lastPrice * (1 + trendFactor + volatility + momentum);
    const confidence = Math.max(0.3, 0.9 - (i * 0.1)); // Decreasing confidence over time
    
    predictions.push({
      date: format(futureDate, 'yyyy-MM-dd'),
      predictedPrice: Math.max(0.1, Number(predictedPrice.toFixed(4))),
      confidence: Number(confidence.toFixed(2)),
      factors: {
        newsSentiment: avgSentiment,
        technicalMomentum: momentum,
        volatility: volatility
      }
    });
  }
  
  return predictions;
};

// Real-time price simulation
export const getCurrentPrice = () => {
  const basePrice = 0.52;
  const volatility = (Math.random() - 0.5) * 0.02;
  return Number((basePrice + volatility).toFixed(4));
};

// News sentiment analysis
export const analyzeSentiment = (newsData) => {
  const sentimentCounts = { positive: 0, negative: 0, neutral: 0 };
  const totalImpact = newsData.reduce((sum, news) => {
    sentimentCounts[news.sentiment]++;
    return sum + Math.abs(news.impact);
  }, 0);
  
  return {
    counts: sentimentCounts,
    totalImpact: Number(totalImpact.toFixed(4)),
    averageImpact: Number((totalImpact / newsData.length).toFixed(4))
  };
};